import React, { useState, useEffect, useMemo } from 'react';
import { Button, Drawer, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useInboundAcceptance } from '@contexts/operator/InboundAcceptanceContext';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import useCan from '@components/shared/permissions/useCan';
import ProductQuickEditDrawerImpl from '@components/shared/products/ProductQuickEditDrawer';
import ProductInfoCardImpl from './drawer/ProductInfoCard';
import ModePageImpl from './drawer/ModePage';
import SelectPageImpl from './drawer/SelectPage';
import FormPage from './drawer/FormPage';

const { Text } = Typography;

interface NavigateOptions {
  mode?: string;
  editingReceiptItem?: unknown;
}

export default function VerificationDrawer() {
  // ProductInfoCard / ModePage / SelectPage / ProductQuickEditDrawer 仍是 .jsx，TS 從其
  // destructure 推導出的 props 不完整（例如自訂屬性 / 多語 props），這裡用 ComponentType<any>
  // 包一層保留原 runtime。放在 component body 內以避免 Vite HMR 下的 top-level TDZ。
  const ProductInfoCard = ProductInfoCardImpl as React.ComponentType<any>;
  const ModePage = ModePageImpl as React.ComponentType<any>;
  const SelectPage = SelectPageImpl as React.ComponentType<any>;
  const ProductQuickEditDrawer = ProductQuickEditDrawerImpl as React.ComponentType<any>;

  const {
    scannedItem, drawerOpen, closeVerification,
    editingReceiptItem, setEditingReceiptItem,
    productAttributeMissing, clearProductAttributeMissing, retryLastAction,
    productAttributeDefinitions,
  } = useInboundAcceptance();
  const { t } = useTranslation();
  const productService = useOperatorProductService();
  const can = useCan();
  const canEditProduct = can('products', 'write');

  const [currentPage, setCurrentPage] = useState<string>('form');
  const [formMode, setFormMode] = useState<string | null>(null);
  const [quickEditOpen, setQuickEditOpen] = useState(false);

  // 顯示用 label：先比對 product_attribute_definitions，找不到 fallback 到 raw name。
  const missingFieldLabels = useMemo(() => {
    if (!productAttributeMissing) return [];
    const defs = productAttributeDefinitions || [];
    return productAttributeMissing.fields.map((name: string) => {
      const def = defs.find((d) => d?.name === name);
      return def?.label || name;
    });
  }, [productAttributeMissing, productAttributeDefinitions]);

  const handleProductQuickEditSuccess = async () => {
    setQuickEditOpen(false);
    // 補完商品主檔後，沿用上一次失敗 action（verify 或 unplanned create）重打。
    // retry 由 context 依 kind 分派並處理成功流程（會關 drawer）。
    if (productAttributeMissing) {
      await retryLastAction();
    }
  };

  const handleMissingModalCancel = () => {
    clearProductAttributeMissing();
  };

  const handleMissingModalFix = () => {
    setQuickEditOpen(true);
  };

  const needsSerialNumbers = ((scannedItem?.required_serial_numbers_count as number | undefined) || 0) > 0;
  const drawerWidth = needsSerialNumbers ? 750 : 680;

  useEffect(() => {
    if (!drawerOpen || !scannedItem) return;

    const isCreateMode = !scannedItem.id;
    const hasBeenVerified = !!scannedItem.received_at && !isCreateMode;
    const receiptItems = (scannedItem.receipt_items as unknown[] | undefined) || [];

    if (hasBeenVerified && receiptItems.length >= 1) {
      setCurrentPage('mode');
      setFormMode(null);
      setEditingReceiptItem(null);
    } else {
      setCurrentPage('form');
      setFormMode(null);
      setEditingReceiptItem(null);
    }
  }, [drawerOpen, scannedItem?.id]);

  const handleNavigate = (page: string, options: NavigateOptions = {}) => {
    if (page === 'close') {
      closeVerification();
      return;
    }

    setCurrentPage(page);

    if (options.mode) {
      setFormMode(options.mode);
    }
    if (options.editingReceiptItem) {
      setEditingReceiptItem(options.editingReceiptItem as any);
    } else if (page === 'mode' || page === 'select') {
      setEditingReceiptItem(null);
    }
  };

  const title = useMemo(() => {
    if (currentPage === 'mode') return t('operator.inbound_item.select_mode');
    if (currentPage === 'select') return t('operator.inbound_item.select_receipt_item');

    const isCreateMode = !scannedItem?.id;
    if (isCreateMode) return t('inbound_item.create_unplanned', { defaultValue: '新增額外項目' });
    return t('operator.verify_item', { defaultValue: '驗收商品' });
  }, [currentPage, scannedItem, t]);

  if (!scannedItem) return null;

  return (
    <>
      <Drawer
        title={title}
        placement="right"
        width={drawerWidth}
        open={drawerOpen}
        onClose={closeVerification}
        destroyOnClose
        styles={{ body: { padding: 24 } }}
      >
        <ProductInfoCard scannedItem={scannedItem} onQuickEdit={() => setQuickEditOpen(true)} />

        {currentPage === 'mode' && (
          <ModePage scannedItem={scannedItem} onNavigate={handleNavigate} />
        )}

        {currentPage === 'select' && (
          <SelectPage scannedItem={scannedItem} onNavigate={handleNavigate} />
        )}

        {currentPage === 'form' && (
          <FormPage
            scannedItem={scannedItem as any}
            formMode={formMode ?? undefined}
            editingReceiptItem={editingReceiptItem as any}
            onNavigate={handleNavigate}
            onSubmitSuccess={closeVerification}
          />
        )}
      </Drawer>

      <ProductQuickEditDrawer
        open={quickEditOpen}
        onClose={() => setQuickEditOpen(false)}
        product={scannedItem ? {
          id: scannedItem.product_id,
          sku: scannedItem.product_sku,
          name: scannedItem.product_name,
        } : null}
        productService={productService}
        onSuccess={handleProductQuickEditSuccess}
      />

      <Modal
        open={!!productAttributeMissing}
        title={t('operator.inbound_item.product_attribute_missing_title', { defaultValue: '商品必填屬性未填寫' })}
        onCancel={handleMissingModalCancel}
        okText={t('operator.inbound_item.product_attribute_missing_fix', { defaultValue: '立即補填' })}
        cancelText={t('common.button.cancel', { defaultValue: '取消' })}
        onOk={handleMissingModalFix}
        footer={canEditProduct ? undefined : [
          <Button key="cancel" onClick={handleMissingModalCancel}>
            {t('common.button.cancel', { defaultValue: '取消' })}
          </Button>,
        ]}
      >
        {canEditProduct ? (
          <Text>
            {t('operator.inbound_item.product_attribute_missing_desc', {
              defaultValue: '以下欄位為貨主設定必填屬性，請補齊後再進行驗收：',
            })}
          </Text>
        ) : (
          <Text>
            {t('operator.inbound_item.product_attribute_missing_no_permission', {
              defaultValue: '沒有修改商品權限，請聯絡貨主補齊以下必填屬性後再進行驗收：',
            })}
          </Text>
        )}
        <ul style={{ marginTop: 12 }}>
          {missingFieldLabels.map((label: string, idx: number) => (
            <li key={idx}>{label}</li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
